import { create } from 'zustand';

/**
 * The last handful of pages this person opened, so the site map can offer them back.
 *
 * Per-browser and best-effort: it lives in localStorage, is cleared on sign out with everything
 * else, and a storage failure simply means no history rather than a broken page.
 */
const RECENT_PAGES_KEY = 'recentPages';
const MAX_PAGES = 10;

export type RecentPage = {
  path: string;
  /** Epoch millis, so the list can be ordered and aged without re-parsing dates. */
  at: number;
};

const read = (): RecentPage[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_PAGES_KEY) ?? '[]');
    return Array.isArray(stored) ? stored.filter(page => typeof page?.path === 'string') : [];
  } catch {
    return [];
  }
};

const write = (pages: RecentPage[]) => {
  try {
    localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(pages));
  } catch {
    // History is a convenience; losing it must never break navigation.
  }
};

export type RecentPagesStore = {
  pages: RecentPage[];
  recordPage: (path: string) => void;
  clearPages: () => void;
};

export const useRecentPagesStore = create<RecentPagesStore>(set => ({
  pages: read(),
  recordPage: path =>
    set(state => {
      // Fix for infinite re rendering issue of the login and ccm page
      if (state.pages[0]?.path === path) return state;

      // Revisiting a page moves it to the front rather than filling the list with repeats.
      const pages = [{ path, at: Date.now() }, ...state.pages.filter(page => page.path !== path)].slice(0, MAX_PAGES);
      write(pages);
      return { pages };
    }),
  clearPages: () =>
    set(() => {
      write([]);
      return { pages: [] };
    })
}));
